import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ExternalLink, LayoutDashboard, LockKeyhole, RadioTower, SatelliteDish, SlidersHorizontal } from "lucide-react";
import { useStudioAuth } from "./StudioAuthContext";
import { ChaserStreamProvider } from "./ChaserStreamContext";
import { Kbd } from "./ui";

const BANNER_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_desk-custom/artifacts/dleu4wtz_IMG_6894.webp";

export const navItems = [
  { to: "/ops", label: "Control Room", icon: LayoutDashboard },
  { to: "/ops/radar", label: "Radar", icon: SatelliteDish },
  { to: "/ops/build", label: "Build", icon: SlidersHorizontal },
  { to: "/ops/output", label: "Output", icon: RadioTower },
];

const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

export const OperationsLayout = () => {
  const { request, lock } = useStudioAuth();
  const location = useLocation();
  const [system, setSystem] = useState(null);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await request("/operations/system");
        const data = await response.json();
        if (!cancelled && response.ok) setSystem(data);
      } catch (_) {}
    };
    load();
    const timer = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [request]);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const healthy = system?.api === "online" && system?.database === "online";
  const active = navItems.find((item) => (item.to === "/ops" ? location.pathname === "/ops" : location.pathname.startsWith(item.to)));

  return (
    <div className="min-h-screen bg-[#01020a] text-white">
      <header className="sticky top-0 z-50 flex h-14 items-center border-b border-[rgba(120,170,255,0.12)] bg-[#050914]">
        <div className="flex h-full w-[200px] shrink-0 items-center border-r border-[rgba(120,170,255,0.12)] px-3">
          <img src={BANNER_IMG} alt="Storm Desk" className="h-9 w-full object-cover object-center" />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-4 px-3 sm:px-4">
          <div className="mono hidden text-[9px] font-bold uppercase tracking-[0.24em] text-slate-500 md:block">Storm Desk Operations</div>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <span data-testid="operations-master-clock" className="mono hidden text-[11px] font-bold tabular-nums text-slate-200 sm:block">
              {clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
            <span data-testid="operations-system-status" className={`mono hidden items-center gap-2 border px-2 py-1 text-[9px] font-bold uppercase tracking-widest sm:flex ${healthy ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-slate-400"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${healthy ? "bg-emerald-400" : "bg-slate-600"}`} /> {healthy ? "Systems online" : "Checking"}
            </span>
            <Link data-testid="operations-public-site-link" to="/" title="Open public site" className="grid h-8 w-8 place-items-center border border-white/10 text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
              <ExternalLink className="h-4 w-4" />
            </Link>
            <button data-testid="operations-lock-button" onClick={lock} title="Lock Studio" className="grid h-8 w-8 place-items-center border border-white/10 text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
              <LockKeyhole className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-3.5rem)]">
        <aside className="hidden w-[200px] shrink-0 border-r border-[rgba(120,170,255,0.12)] bg-[#050914] lg:flex lg:flex-col">
          <nav className="sticky top-14 p-2">
            <div className="mono px-2 py-2 text-[9px] font-bold uppercase tracking-[0.24em] text-slate-600">Workspace</div>
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/ops"}
                data-testid={`operations-nav-${testId(label)}`}
                className={({ isActive }) => `mb-1 flex h-10 items-center gap-3 border-l-2 px-3 text-sm font-semibold transition-colors ${isActive ? "border-sky-500 bg-sky-500/10 text-white" : "border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-white"}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}

            <div className="mt-6 border-t border-[rgba(120,170,255,0.12)] pt-3">
              <div className="mono px-2 text-[9px] font-bold uppercase tracking-[0.24em] text-slate-600">Shortcuts</div>
              <ul className="mono mt-2 space-y-1.5 px-2 text-[9px] uppercase tracking-wider text-slate-500">
                <li className="flex items-center justify-between gap-2"><span>Take</span><Kbd>Space</Kbd></li>
                <li className="flex items-center justify-between gap-2"><span>Scenes</span><Kbd>1–6</Kbd></li>
                <li className="flex items-center justify-between gap-2"><span>Mute all</span><Kbd>M</Kbd></li>
              </ul>
            </div>
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-20 lg:pb-0"><ChaserStreamProvider><Outlet /></ChaserStreamProvider></main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-50 flex h-16 items-center border-t border-[rgba(120,170,255,0.12)] bg-[#050914] px-2 lg:hidden">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/ops"}
            data-testid={`operations-mobile-nav-${testId(label)}`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 px-1 py-2 text-[10px] font-semibold ${isActive ? "text-sky-400" : "text-slate-500"}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>

      <span className="sr-only" data-testid="operations-active-workspace">{active?.label || "Operations"}</span>
    </div>
  );
};
